import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAgencyMfaPolicy,
  updateAgencyMfaActivationDate,
  updateAgencyMfaMatrix,
} from 'api/requests/agency-mfa-requests';
import {
  AGENCY_MFA__CLEAR,
  AGENCY_MFA__DISCARD_DRAFT,
  AGENCY_MFA__GET,
  AGENCY_MFA__SAVE,
  AGENCY_MFA__SET_DRAFT,
  AGENCY_MFA__SET_ERROR,
  AGENCY_MFA__SET_LOADING,
  AGENCY_MFA__SET_POLICY,
  AGENCY_MFA__SET_SAVED,
  AGENCY_MFA__SET_SAVING,
} from 'store/constants';
import { SECURITY__LOAD_ERROR, SECURITY__SAVE_ERROR } from 'translations/constants';
import { IAgencyMfaPolicy } from 'types/mfa/IAgencyMfaPolicy';

export const setAgencyMfaPolicyAction = createAction<IAgencyMfaPolicy>(AGENCY_MFA__SET_POLICY);
export const setAgencyMfaSavedAction = createAction<IAgencyMfaPolicy>(AGENCY_MFA__SET_SAVED);
export const setAgencyMfaDraftAction = createAction<IAgencyMfaPolicy>(AGENCY_MFA__SET_DRAFT);
export const discardAgencyMfaDraftAction = createAction<void>(AGENCY_MFA__DISCARD_DRAFT);
export const setAgencyMfaLoadingAction = createAction<boolean>(AGENCY_MFA__SET_LOADING);
export const setAgencyMfaSavingAction = createAction<boolean>(AGENCY_MFA__SET_SAVING);
export const setAgencyMfaErrorAction = createAction<string | null>(AGENCY_MFA__SET_ERROR);
export const clearAgencyMfaAction = createAction<void>(AGENCY_MFA__CLEAR);

export const getAgencyMfaPolicyAction = createAsyncThunk(AGENCY_MFA__GET, async (_: void, thunkApi) => {
  thunkApi.dispatch(setAgencyMfaLoadingAction(true));
  thunkApi.dispatch(setAgencyMfaErrorAction(null));
  try {
    const policy = await getAgencyMfaPolicy();
    thunkApi.dispatch(setAgencyMfaPolicyAction(policy));
  } catch {
    // The API returns a bare 403 with no body for non-SuperAdmins and plain-text diagnostics for
    // 400s. Neither is user copy, so every failure maps to the same translated message.
    thunkApi.dispatch(setAgencyMfaErrorAction(SECURITY__LOAD_ERROR));
  } finally {
    thunkApi.dispatch(setAgencyMfaLoadingAction(false));
  }
});

interface SaveAgencyMfaPolicyArgs {
  policy: IAgencyMfaPolicy;
  matrixDirty: boolean;
  dateDirty: boolean;
}

/**
 * Saves only the dirty sections. The matrix goes first so that, if the date write then fails, the
 * stronger of the two changes is already stored rather than silently dropped.
 *
 * On any failure the draft is deliberately left alone — the form stays dirty — and the policy is
 * re-read so `saved` reflects what the server actually holds. Without that re-read a partial save
 * would leave the screen claiming a state the server never accepted.
 */
export const saveAgencyMfaPolicyAction = createAsyncThunk(
  AGENCY_MFA__SAVE,
  async (args: SaveAgencyMfaPolicyArgs, thunkApi) => {
    const { policy, matrixDirty, dateDirty } = args;
    thunkApi.dispatch(setAgencyMfaSavingAction(true));
    thunkApi.dispatch(setAgencyMfaErrorAction(null));
    try {
      if (matrixDirty) {
        await updateAgencyMfaMatrix(policy.cells);
      }
      if (dateDirty) {
        await updateAgencyMfaActivationDate(policy.mandatoryActivationDate);
      }
      thunkApi.dispatch(setAgencyMfaPolicyAction(policy));
    } catch {
      thunkApi.dispatch(setAgencyMfaErrorAction(SECURITY__SAVE_ERROR));
      const current = await getAgencyMfaPolicy().catch(() => null);
      if (current) {
        thunkApi.dispatch(setAgencyMfaSavedAction(current));
      }
    } finally {
      thunkApi.dispatch(setAgencyMfaSavingAction(false));
    }
  },
);
